import React, { useEffect, useRef, useState } from "react";
import { BEFORE_AFTER } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Interactive before/after comparison slider.
 * Drag the divider or use the range input to reveal the "after" state.
 */
function CompareSlider({ before, after, label, testId }) {
  const [pct, setPct] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = Math.max(2, Math.min(98, ((clientX - rect.left) / rect.width) * 100));
    setPct(p);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <div
      data-testid={testId}
      ref={ref}
      className="relative aspect-[4/3] w-full select-none overflow-hidden rounded-2xl border border-black/10 bg-[#EBEDF0]"
      onMouseDown={(e) => {
        dragging.current = true;
        setFromClientX(e.clientX);
      }}
      onTouchStart={(e) => {
        dragging.current = true;
        setFromClientX(e.touches[0].clientX);
      }}
    >
      {/* After (base) */}
      <img
        src={after}
        alt={`${label}, after`}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Before (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${label}, before`}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>

      {/* Labels */}
      <span className="pointer-events-none absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white backdrop-blur">
        Before
      </span>
      <span className="pointer-events-none absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-black backdrop-blur">
        After
      </span>

      {/* Divider handle */}
      <div
        className="absolute inset-y-0 z-10 -ml-px w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `${pct}%` }}
      >
        <button
          type="button"
          aria-label={`Reveal after, ${label}`}
          data-testid={`${testId}-handle`}
          className="absolute top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white text-black shadow-[0_10px_30px_-8px_rgba(0,0,0,0.6)] transition hover:scale-105"
          onMouseDown={(e) => {
            e.stopPropagation();
            dragging.current = true;
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
            dragging.current = true;
          }}
        >
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center gap-1 font-display text-sm font-bold">
            ⟵ ⟶
          </span>
        </button>
      </div>
    </div>
  );
}

export default function BeforeAfter({ compact = false }) {
  const items = compact ? BEFORE_AFTER.slice(0, 2) : BEFORE_AFTER;
  return (
    <section
      data-testid="before-after-section"
      className="section-lw relative overflow-hidden bg-[#F4F5F7] text-black"
    >
      <div className="container-lw">
        <div className="mx-auto max-w-3xl text-center">
          <div className="eyebrow justify-center text-[#4F0DFF]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
            Before / After proof
          </div>
          <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
            The difference is <span className="text-gradient-lw">visible</span>{" "}
            before it is explained.
          </h2>
          <p className="mt-5 text-[17px] leading-relaxed text-[#4A5359]">
            Buyers notice the standard before they know how it was created.
            What looks handled properly feels easier to trust. Drag the
            divider to compare.
          </p>
        </div>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          {items.map((item) => (
            <div key={item.slug} className="animate-fade-up">
              <CompareSlider
                before={`/generated/${item.slug}-before.png`}
                after={`/generated/${item.slug}-after.png`}
                label={item.label}
                testId={`compare-${item.slug}`}
              />
              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <div className="font-display text-lg font-semibold">
                    {item.year} {item.trim}
                  </div>
                  <div className="text-sm text-[#79858D]">
                    Same unit. Same lot. Different standard.
                  </div>
                </div>
                <div className="text-xs uppercase tracking-widest text-[#79858D]">
                  {item.slug.replace("-", " · ")}
                </div>
              </div>
            </div>
          ))}
        </div>

        {compact && (
          <div className="mt-14 flex justify-center">
            <Link
              to="/sample-listings"
              data-testid="before-after-view-more"
              className="btn-ghost-lw"
            >
              View more sample listings
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
